import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSurveyRoutes } from '../hooks/useSurveyRoutes';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/tools/makeStyles';
import combineStyles from 'classnames';
import Typography from '@mui/material/Typography';
import OldThemedButton from '../components/OldThemedButton';
import { selectInvalidQuestions } from '../redux/selectors/survey_selectors';
import { createUrl } from '../helpers/router_helper';
import { submitSurvey, gotoQuestion, gotoSurveyPage } from '../redux/actions';
import { hasMatchedCondition } from '../helpers/logic_helper';
import { Button } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../redux/ReduxProvider';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  surveyIsSubmitting: {
    opacity: 0.3,
  },
  messageContainer: {
    textAlign: 'center',
    marginBottom: 16,
  },
  message: {
    fontSize: 32,
    color: theme.palette.mainaccent,
  },
  errorMessage: {
    color: theme.palette.unfavorable,
  },
  btn: {
    height: 48,
    marginRight: 8,
    '&:hover': {
      backgroundColor: '#0B4E54',
    },
  },
  backBtn: {
    color: '#0F7B85',
    backgroundColor: theme.palette.common.white,
    height: 48,
    marginRight: 8,
    '&:hover': {
      backgroundColor: '#F3FBFC',
    },
    display: 'inline-flex',
    alignItems: 'center',
    border: 0,
    borderRadius: 4,
  },
}));

const SubmitPage = () => {
  const styles = useStyles();
  const { routes, surveyCode } = useSurveyRoutes();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    isSubmitting: surveyIsSubmitting,
    isSubmitted: surveyIsSubmitted,
    isExpired: surveyExpired,
    showSubmitPage,
    error,
    openSurvey,
    submitErrorKey,
    canRetrySubmit,
  } = useAppSelector((state) => state.survey);

  const answers = useAppSelector((state) => state.answers);
  const questions = useAppSelector((state) => state.questions);
  const hasNoApplicableQuestions = questions.length === 0;

  const invalidQuestions = useAppSelector(selectInvalidQuestions);
  const visibleInvalidQuestions = invalidQuestions.filter((q) => {
    if (!q.displayConditions || q.displayConditions.length <= 0) return true;
    const isVisible = hasMatchedCondition(q.displayConditions, answers);
    return isVisible;
  });
  const hasVisibleInvalidQuestions = visibleInvalidQuestions.length > 0;

  const errorMessage = error || (submitErrorKey ? t(submitErrorKey) : '');
  const canSubmit = !openSurvey || !errorMessage || canRetrySubmit;

  useEffect(() => {
    if (!showSubmitPage) navigate(createUrl(routes.survey, [surveyCode], { queryParams: location.search }));
    // eslint-disable-next-line
  }, [showSubmitPage]);

  useEffect(() => {
    if (hasVisibleInvalidQuestions) {
      dispatch(gotoQuestion(visibleInvalidQuestions[0].id));
    }
    if (showSubmitPage && (hasVisibleInvalidQuestions || surveyExpired || surveyIsSubmitted)) {
      dispatch(gotoSurveyPage());
    }
    // eslint-disable-next-line
  }, [hasVisibleInvalidQuestions, showSubmitPage, surveyExpired, surveyIsSubmitted]);

  const onClickReturnToSurvey = (e) => {
    if (e) e.preventDefault();
    dispatch(gotoSurveyPage());
  };

  const onClickSubmit = (e) => {
    if (e) e.preventDefault();
    dispatch(submitSurvey(surveyCode));
  };

  return (
    <div
      className={combineStyles(styles.root, {
        [styles.surveyIsSubmitting]: surveyIsSubmitting,
      })}
    >
      {hasNoApplicableQuestions ? (
        <NotApplicablePartial />
      ) : !errorMessage ? (
        <SubmitPartial
          surveyIsSubmitting={surveyIsSubmitting}
          onClickReturnToSurvey={onClickReturnToSurvey}
          onClickSubmit={onClickSubmit}
        />
      ) : (
        <ErrorPartial
          errorMessage={errorMessage}
          canSubmit={canSubmit}
          surveyIsSubmitting={surveyIsSubmitting}
          onClickReturnToSurvey={onClickReturnToSurvey}
          onClickSubmit={onClickSubmit}
        />
      )}
    </div>
  );
};

const NotApplicablePartial = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const announceRef = useRef(null);

  useEffect(() => {
    if (announceRef?.current) {
      announceRef.current.focus();
    }
  }, []);

  return (
    <div className={styles.messageContainer}>
      <Typography tabIndex={0} ref={announceRef} variant="h1" className={styles.message}>
        {t('survey.not-applicable.title')}
      </Typography>
      <Typography tabIndex={0}>{t('survey.not-applicable.message')}</Typography>
    </div>
  );
};

const SubmitPartial = ({ surveyIsSubmitting, onClickReturnToSurvey, onClickSubmit }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.messageContainer}>
        <Typography className={styles.message}>{t('survey.confirm')}</Typography>
      </div>

      <CallToActions
        surveyIsSubmitting={surveyIsSubmitting}
        onClickReturnToSurvey={onClickReturnToSurvey}
        onClickSubmit={onClickSubmit}
      />
    </>
  );
};

const ErrorPartial = ({ errorMessage, canSubmit, surveyIsSubmitting, onClickReturnToSurvey, onClickSubmit }) => {
  const styles = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.messageContainer}>
        <Typography className={combineStyles(styles.message, styles.errorMessage)}>
          {t('survey.error.title')}
        </Typography>
        <Typography>{errorMessage}</Typography>
      </div>

      <CallToActions
        surveyIsSubmitting={surveyIsSubmitting}
        onClickReturnToSurvey={onClickReturnToSurvey}
        onClickSubmit={onClickSubmit}
        canSubmit={canSubmit}
        isRetry
      />
    </>
  );
};

const CallToActions = ({
  surveyIsSubmitting,
  onClickReturnToSurvey,
  onClickSubmit,
  canSubmit = true,
  isRetry = false,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const questions = useAppSelector((state) => state.questions);

  const submitLabels = {
    tryAgain: t('survey.tryagain'),
    submit: t('survey.submit'),
  };
  const submitLabel = isRetry ? submitLabels.tryAgain : submitLabels.submit;

  return (
    <div>
      {!!questions.length && (
        <Button className={styles.backBtn} disabled={surveyIsSubmitting} onClick={onClickReturnToSurvey}>
          {t('survey.return')}
        </Button>
      )}

      {canSubmit && (
        <OldThemedButton className={styles.btn} disabled={surveyIsSubmitting} onClick={onClickSubmit}>
          {submitLabel}
        </OldThemedButton>
      )}
    </div>
  );
};

export default SubmitPage;
